import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminLaunchStatusResponse } from '@find-my-part/types';
import { adminApi } from '../api/client';
import { WORKBENCH_STALE_AFTER_MS, type WorkbenchFreshness } from './usePendingWork';

export interface LaunchStatusSnapshot {
  data: AdminLaunchStatusResponse | null;
  freshness: WorkbenchFreshness;
  refreshing: boolean;
  announcement: string;
  announcementRevision: number;
  refresh: () => Promise<void>;
}

/** Minimal shell-wide launch-status read for the persistent launch-mode chip. */
export function useLaunchStatus(
  token: string | null,
  enabled: boolean,
  invalidationVersion: number
): LaunchStatusSnapshot {
  const [data, setData] = useState<AdminLaunchStatusResponse | null>(null);
  const dataRef = useRef<AdminLaunchStatusResponse | null>(null);
  const [freshness, setFreshness] = useState<WorkbenchFreshness>('unavailable');
  const [refreshing, setRefreshing] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [announcementRevision, setAnnouncementRevision] = useState(0);
  const [receivedAt, setReceivedAt] = useState<number | null>(null);

  const announce = useCallback((message: string): void => {
    setAnnouncement(message);
    setAnnouncementRevision((revision) => revision + 1);
  }, []);

  const requestGenerationRef = useRef(0);
  const refresh = useCallback(async (): Promise<void> => {
    if (!token || !enabled) return;
    const generation = ++requestGenerationRef.current;
    // A mode is truthful only while its projection is current. Invalidation
    // immediately makes last-good data stale/unknown until the new read lands.
    setFreshness(dataRef.current ? 'stale' : 'unavailable');
    setRefreshing(true);
    try {
      const response = await adminApi.launchStatus.get(token);
      if (!response.success) throw new Error('Launch status unavailable');
      if (generation !== requestGenerationRef.current) return;
      dataRef.current = response.data;
      setData(response.data);
      setReceivedAt(Date.now());
      setFreshness('fresh');
      announce('Public launch status updated.');
    } catch {
      if (generation !== requestGenerationRef.current) return;
      setFreshness(dataRef.current ? 'stale' : 'unavailable');
      announce('Public launch status is unavailable and is shown as unknown.');
    } finally {
      if (generation === requestGenerationRef.current) {
        setRefreshing(false);
      }
    }
  }, [announce, enabled, token]);

  useEffect(() => {
    if (!enabled || !token) {
      requestGenerationRef.current += 1;
      dataRef.current = null;
      setData(null);
      setReceivedAt(null);
      setFreshness('unavailable');
      setRefreshing(false);
      setAnnouncement('');
      return;
    }
    void refresh();
  }, [enabled, invalidationVersion, refresh, token]);

  useEffect(() => {
    if (freshness !== 'fresh' || receivedAt === null) return;
    const timer = window.setTimeout(() => {
      setFreshness('stale');
      announce('Public launch status is stale and is now shown as unknown.');
    }, WORKBENCH_STALE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [announce, freshness, receivedAt]);

  return { data, freshness, refreshing, announcement, announcementRevision, refresh };
}
